import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Privacy Policy',
  lastUpdated: 'Last updated: January 2025',
  sections: [{
    heading: 'Information We Collect',
    body: 'We collect only the information necessary to process your purchase, including your name, email address, and payment details. We do not collect any unnecessary personal data.'
  }, {
    heading: 'How We Use Your Information',
    body: 'Your information is used solely to process your order, deliver your digital product, and send you a purchase confirmation. We will never sell, rent, or share your personal information with third parties for marketing purposes.'
  }, {
    heading: 'Payment Security',
    body: 'All payments are processed through secure, encrypted third-party payment processors. We do not store your credit card or payment information on our servers.'
  }, {
    heading: 'Digital Product Delivery',
    body: 'Upon successful payment, your e-book will be delivered instantly via a download link. Your email may be used to send a backup copy of the download link.'
  }, {
    heading: 'Cookies',
    body: 'Our website may use essential cookies to ensure proper functionality. We do not use tracking cookies or third-party advertising cookies.'
  }, {
    heading: 'Your Rights',
    body: 'You have the right to request access to, correction of, or deletion of your personal data at any time. To exercise these rights, please reach out through our available communication channels.'
  }]
};
const termsContent = {
  title: 'Terms of Service',
  lastUpdated: 'Last updated: January 2025',
  sections: [{
    heading: 'Digital Product',
    body: 'This is a digital product (PDF e-book) delivered electronically. By completing your purchase, you agree to these terms and acknowledge that you are purchasing a downloadable digital product.'
  }, {
    heading: 'License & Usage',
    body: 'Upon purchase, you are granted a personal, non-transferable license to use the e-book for personal and family use. You may not redistribute, resell, or share the e-book with others.'
  }, {
    heading: 'Refund Policy',
    body: 'Due to the digital nature of this product, all sales are final. Once the e-book has been downloaded, refunds cannot be issued. If you experience technical issues with your download, we will work to resolve them promptly.'
  }, {
    heading: 'Intellectual Property',
    body: 'All content within the e-book, including text, illustrations, and design, is the intellectual property of All Open Wellness and is protected by copyright law.'
  }, {
    heading: 'Disclaimer',
    body: 'The activities in this e-book are designed for children ages 3–6 under adult supervision. All Open Wellness is not responsible for any injuries or damages that may occur during the activities. Parental supervision is always recommended.'
  }, {
    heading: 'Changes to Terms',
    body: 'We reserve the right to update these terms at any time. Any changes will be reflected on this page with an updated revision date.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="py-10 border-t border-brand-sage/20" data-id="element-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-4" data-id="element-17">
          <div className="flex items-center gap-6" data-id="element-18">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-brand-gray-warm hover:text-brand-terracotta font-medium transition-colors duration-200 underline underline-offset-2 decoration-brand-sage/30 hover:decoration-brand-terracotta" data-id="element-19">
              Privacy Policy
            </button>
            <span className="text-brand-sage/40" data-id="element-20">•</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-brand-gray-warm hover:text-brand-terracotta font-medium transition-colors duration-200 underline underline-offset-2 decoration-brand-sage/30 hover:decoration-brand-terracotta" data-id="element-21">
              Terms of Service
            </button>
          </div>
          <p className="text-sm text-brand-gray-warm/70 font-medium" data-id="element-22">
            All Open Wellness &copy; {new Date().getFullYear()}
          </p>
        </div>
      </footer>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-23">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-brown-dark/40 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-24">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-brand-cream rounded-3xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col border border-brand-sage/10" onClick={e => e.stopPropagation()} data-id="element-25">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-8 pt-8 pb-4 border-b border-brand-sage/10" data-id="element-26">
                <div data-id="element-27">
                  <h2 className="text-2xl font-bold text-brand-brown-dark font-heading" data-id="element-28">
                    {content.title}
                  </h2>
                  <p className="text-sm text-brand-gray-warm/60 mt-1" data-id="element-29">
                    {content.lastUpdated}
                  </p>
                </div>
                <button onClick={() => setActiveModal(null)} className="w-10 h-10 rounded-full bg-brand-sage/10 hover:bg-brand-sage/20 flex items-center justify-center transition-colors duration-200 flex-shrink-0" aria-label="Close" data-id="element-30">
                  <XIcon className="w-5 h-5 text-brand-brown-dark" data-id="element-31" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6 overflow-y-auto flex-1" data-id="element-32">
                <div className="space-y-6" data-id="element-33">
                  {content.sections.map((section, idx) => <div key={idx} data-id="element-34">
                      <h3 className="text-lg font-bold text-brand-brown-dark mb-2 font-heading" data-id="element-35">
                        {section.heading}
                      </h3>
                      <p className="text-brand-gray-warm leading-relaxed" data-id="element-36">
                        {section.body}
                      </p>
                    </div>)}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-8 py-5 border-t border-brand-sage/10" data-id="element-37">
                <button onClick={() => setActiveModal(null)} className="w-full py-3 bg-brand-sage/10 hover:bg-brand-sage/20 text-brand-brown-dark rounded-full font-semibold transition-colors duration-200" data-id="element-38">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}